import { Box } from "../../../components/ui/box";
import type { SurfaceToken } from "../../../components/ui/box-classes";
import { Card } from "../../../components/ui/card";
import { DELAYED_FADE, Skeleton } from "../../../components/ui/skeleton";
import { useElementSize } from "../../../hooks/use-element-size";
import { useIsMobile } from "../../../hooks/use-media-query";
import type { Omittable } from "../../../lib/props";
import { cn } from "../../../lib/utils";
import { GAP_CLASSES, type GridColumns, type SpacingToken } from "../../sortable-grid";
import { SINGLE_COLUMN_MIN_WIDTH } from "../widget-grid";

/** One anonymous tile of a loading board: a grid span, nothing else. */
export type WidgetGridSkeletonTile = { w: number; h: number };

export type WidgetGridSkeletonProps = Omittable<{
  /** The tile silhouette, in grid spans. Tiles auto-place in order; a span
   *  wider than `columns` clamps to full width. */
  tiles: readonly WidgetGridSkeletonTile[];
  /** Mirrors the `WidgetGrid` the board will render into. */
  columns?: GridColumns;
  rowHeight?: number;
  gap?: SpacingToken;
  /** The surface the real cards will paint, so the tiles sit on the board
   *  exactly where the cards land. */
  surface?: SurfaceToken;
  /** Invisible for the first ~200ms, then fades in as one — a warm cache or a
   *  fast query swaps the real board in before any tile paints. */
  delayed?: boolean;
}>;

/** Mirrors `WidgetGrid`'s own defaults. */
const DEFAULT_COLUMNS: GridColumns = 12;
const DEFAULT_ROW_HEIGHT = 60;

/** Bars a `rows` body lays — uneven through `vary`, so a loading list reads
 *  as lines of text and never as a table. */
const ROW_COUNT = 4;

/**
 * The first-paint stand-in for a whole widget board: card-shaped tiles in the
 * grid's own rhythm, each carrying a header rung and an anonymous body. The
 * settled geometry lives in the board's stored rows, so the silhouette never
 * promises a specific layout — every tile is the same card, the body is bars
 * that could be any widget, and the arrangement is the host's guess at a
 * plausible board. The point is the rhythm, not the match: a page that paints
 * tiles then cards reads as arriving, where an empty fill that suddenly holds
 * a board reads as a flash.
 *
 * Stacks single-column exactly where `WidgetGrid` does — the board's OWN width
 * under `SINGLE_COLUMN_MIN_WIDTH`, or a mobile viewport — so a narrow board
 * never skeletons twelve columns it will not render.
 */
export function WidgetGridSkeleton({
  tiles,
  columns = DEFAULT_COLUMNS,
  rowHeight = DEFAULT_ROW_HEIGHT,
  gap = "md",
  surface = "elevated",
  delayed = false,
}: WidgetGridSkeletonProps) {
  const isMobile = useIsMobile();
  const { ref, size } = useElementSize<HTMLDivElement>();
  const singleColumn =
    isMobile || (size.width > 0 && size.width < SINGLE_COLUMN_MIN_WIDTH);

  return (
    <Box
      ref={ref}
      display="grid"
      columns={singleColumn ? 1 : columns}
      className={cn(GAP_CLASSES[gap], delayed && DELAYED_FADE)}
      style={{ gridAutoRows: `${rowHeight}px` }}
      data-slot="widget-grid-skeleton"
      aria-hidden
    >
      {tiles.map((tile, index) => {
        const w = singleColumn ? 1 : Math.min(tile.w, columns);
        const h = Math.max(1, Math.round(tile.h));
        return (
          <Card
            key={index}
            radius="lg"
            surface={surface}
            direction="column"
            minHeight={0}
            style={{ gridColumn: `span ${w}`, gridRow: `span ${h}` }}
            data-slot="widget-grid-skeleton-tile"
          >
            {/* The header rung a grid card holds at rest (36px) with a
                title-length bar; the body inset matches the card's `md`. */}
            <Box direction="row" align="center" minHeight={36} paddingX="md" paddingTop="sm">
              <Skeleton height={12} width="40%" />
            </Box>
            <Box direction="column" grow minHeight={0} gap="sm" paddingX="md" paddingBottom="md" overflow="hidden">
              {w >= columns && !singleColumn ? (
                // A full-width tile silhouettes a stat band: three figures
                // side by side, the shape a hero card most often takes.
                <Box direction="row" gap="lg" grow align="center">
                  {[0, 1, 2].map((i) => (
                    <Box key={i} direction="column" gap="sm" flex={1}>
                      <Skeleton height={10} width="48%" />
                      <Skeleton height={28} width="64%" />
                    </Box>
                  ))}
                </Box>
              ) : (
                Array.from({ length: ROW_COUNT }, (_, i) => (
                  <Skeleton height={12} key={i} vary={i} width="100%" />
                ))
              )}
            </Box>
          </Card>
        );
      })}
    </Box>
  );
}
